import SheetClipCls from 'sheetclip'
import { User } from '@global/types'
import { RowPropertyName } from './readGridToolbar/components/toggleColumns/types'

const SheetClip = new SheetClipCls()

interface Column {
  fieldId: number
  rowPropertyName?: RowPropertyName
}

interface CellData {
  value: string | User
}

interface SelectionArgs {
  data: Column[][]
  selection: string[]
  columns: Column[]
  multiSelect: boolean
}

type Selections = Record<string, string>

/**
 * Get data within the range of the selection.
 * @param args
 * @param {Array<Column[]>} args.data
 * @param {string[]} args.selection
 * @param {Column[]} args.columns
 * @returns {Selections} Return an object of all the selections and their values.
 */
export function getDataForSelection({
  data,
  selection,
  columns,
  multiSelect,
}: SelectionArgs): Selections {
  const selectors = Object.keys(selection)
  const returnObject = {}

  selectors.forEach(selector => {
    const [row, col] = selector.split('_')
    if (!row || !col) {
      throw new Error('Invalid selector', { cause: { selector } })
    }
    const acutalCol = multiSelect ? +col - 2 : +col - 1
    const cellData = getCellData({ data, columns, row, col: acutalCol })

    returnObject[selector] = cellData
  })

  return returnObject
}

function getCellData({
  data,
  columns,
  row,
  col,
}: {
  data: Column[][]
  columns: Column[]
  row: number | string
  col: number
}): CellData {
  const rowPropertyName = columns[col]?.rowPropertyName

  if (rowPropertyName) {
    const properties = data[row]?.properties
    const propertyValue = properties?.[rowPropertyName]

    if (rowPropertyName === RowPropertyName.cvId) {
      return { value: data[row]?.cvid }
    }

    if (
      rowPropertyName === RowPropertyName.createdBy ||
      rowPropertyName === RowPropertyName.updatedBy
    ) {
      return { value: propertyValue.name }
    }

    return { value: propertyValue }
  }

  return data[row]?.data[columns[col]?.fieldId]
}

/**
 * Create a string of all values in a selection; for copy and paste.
 */
export function selectionDataToCopy({ data }: { data: { value: `${number}_${number}` } }): string {
  const selectors = Object.keys(data)
  let minRow
  let minColumn

  selectors.forEach(val => {
    const pieces = val.split('_')

    if (!pieces[0] || !pieces[1]) {
      throw new Error('Invalid piece', { cause: { piece: val } })
    }

    const row = +pieces[0]
    const col = +pieces[1]
    if (!minRow || minRow > row) {
      minRow = row
    }
    if (!minColumn || minColumn > col) {
      minColumn = col
    }
  })

  const loopData: unknown[][] = []
  // make an array of arrays to join the data on with fancy characters.
  selectors.forEach(sel => {
    const pieces = sel.split('_')

    if (!pieces[0] || !pieces[1]) {
      throw new Error('Invalid piece', { cause: { piece: sel } })
    }

    const row = +pieces[0]
    const col = +pieces[1]
    const value = data[sel] && typeof data[sel].value === 'string' ? data[sel].value : ''
    const relativeRow = row - minRow
    const relativeColumn = col - minColumn
    if (!loopData[relativeRow]) {
      loopData[relativeRow] = []
    }
    loopData[relativeRow][relativeColumn] = value
  })

  const friendlyToExcel = SheetClip.stringify(loopData)

  return friendlyToExcel.slice(0, -1)
}
